import { useEffect, useState, useMemo } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { PieChart } from "@mui/x-charts/PieChart";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { useRefreshInterval } from "../../contexts/RefreshIntervalContext";
import { getCurrentTenant } from "../../config/tenantConfig";
import { usePageVisibility } from "../../hooks/usePageVisibility";
import { getRegion } from "../../api/regionAPI";
import { getNode } from "../../api/nodeAPI";

const StyledText = styled("text", {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme }) => ({
  textAnchor: "middle",
  dominantBaseline: "central",
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: {
        variant: "primary",
      },
      style: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    {
      props: ({ variant }) => variant !== "primary",
      style: {
        fontSize: theme.typography.body2.fontSize,
      },
    },
    {
      props: {
        variant: "primary",
      },
      style: {
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== "primary",
      style: {
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

Level2RegionChart.propTypes = {
  onRegionSelect: PropTypes.func,
  selectedRegionId: PropTypes.number,
};

// Renk paleti - Level 2 için (sıcak tonlar)
const COLOR_PALETTE_LEVEL2 = [
  "#FF6B6B", // Kırmızı
  "#FF8E53", // Turuncu
  "#FFA726", // Koyu Turuncu
  "#FFB74D", // Açık Turuncu
  "#FFD93D", // Sarı
  "#F06292", // Pembe
  "#BA68C8", // Mor
  "#9575CD", // Açık Mor
  "#E57373", // Açık Kırmızı
  "#FFB300", // Amber
];

// Renk paleti - Level 3 için (soğuk tonlar)
const COLOR_PALETTE_LEVEL3 = [
  "#42A5F5", // Mavi
  "#26C6DA", // Cyan
  "#66BB6A", // Yeşil
  "#26A69A", // Teal
  "#29B6F6", // Açık Mavi
  "#5C6BC0", // Indigo
  "#78909C", // Blue Grey
  "#4DB6AC", // Açık Teal
  "#4FC3F7", // Light Blue
  "#7E57C2", // Deep Purple
];

// Region'un level'ını hesaplayan fonksiyon
const calculateRegionLevel = (region, allRegions) => {
  let level = 1;
  let currentRegion = region;

  while (currentRegion.parentRegion) {
    const parent = allRegions.find(
      (r) => r.idRegion === currentRegion.parentRegion
    );
    if (!parent) break;
    level++;
    currentRegion = parent;
    if (level > 10) break; // Sonsuz döngü koruması
  }

  return level;
};

// Level 2 Region Chart - Level 2'lerin içindeki Level 3 sayılarını gösterir
export function Level2RegionChart({ onRegionSelect, selectedRegionId }) {
  const { t } = useTranslation();
  const { isRunning } = useRefreshInterval();
  const isPageVisibility = usePageVisibility();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true); // Sadece ilk yükleme için
  const [error, setError] = useState(null);
  const [level2Data, setLevel2Data] = useState([]);

  // Verileri çek
  const fetchData = async (isInitialLoad = false) => {
    // Sadece ilk yüklemede loading göster
    if (isInitialLoad) {
      setLoading(true);
    }
    setError(null);
    try {
      const regionsData = await getRegion();
      setRegions(regionsData || []); // State güncellenir ama UI sarsılmaz
    } catch (err) {
      setError(err.message || t('common.errorLoadingData', 'Veri yüklenirken hata oluştu'));
      console.error("Error fetching data:", err);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData(true); // İlk yükleme - Level2RegionChart
  }, []);

  // Auto refresh
  useEffect(() => {
    let interval;
    if (isRunning && isPageVisibility) {
      interval = setInterval(() => fetchData(false), 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, isPageVisibility]);

  // RefreshAllData event listener
  useEffect(() => {
    const handler = () => {
      fetchData(false);
    };
    window.addEventListener("refreshAllData", handler);
    return () => window.removeEventListener("refreshAllData", handler);
  }, []);

  // 401 hatası kontrolü
  useEffect(() => {
    if (error && error.includes("401")) {
      console.log("Authentication error (401) detected, logging out user");
      localStorage.removeItem("authToken");
      window.location.href = "/login";
    }
  }, [error]);

  // Level 2 region'ları ve içlerindeki Level 3 sayısını hesapla
  useEffect(() => {
    if (regions.length > 0) {
      // Level 2 region'ları filtrele
      const level2Regions = regions.filter(
        (region) => calculateRegionLevel(region, regions) === 2
      );

      // Her Level 2 için içindeki Level 3'leri say
      const level2Counts = level2Regions.map((level2Region, index) => {
        // Bu Level 2'nin child'ları olan Level 3'leri bul
        const level3Count = regions.filter(
          (region) =>
            region.parentRegion === level2Region.idRegion &&
            calculateRegionLevel(region, regions) === 3
        ).length;

        return {
          idRegion: level2Region.idRegion,
          name:
            level2Region.name ||
            level2Region.code ||
            `Region ${level2Region.idRegion}`,
          level3Count: level3Count,
          color:
            level3Count > 0
              ? COLOR_PALETTE_LEVEL2[index % COLOR_PALETTE_LEVEL2.length]
              : "#9E9E9E",
          isEmpty: level3Count === 0,
        };
      });

      setLevel2Data(level2Counts);
    }
  }, [regions]);

  // Chart için veri hazırla
  const chartData = level2Data
    .filter((item) => item.level3Count > 0)
    .map((item) => ({
      label: item.name,
      value: item.level3Count,
      color: item.color,
    }));

  const totalLevel3Regions = level2Data.reduce(
    (sum, item) => sum + item.level3Count,
    0
  );

  const emptyLevel2Count = level2Data.filter((item) => item.isEmpty).length;

  return (
    <Paper
      elevation={3}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        overflow: "auto",
        maxHeight: "800px",
        p: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{ mb: 1.5, fontSize: "1.1rem", fontWeight: 600 }}
      >
        {t("regions.charts.level2Distribution", "Bölge - Alt Bölge")}
      </Typography>

      {chartData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PieChart
              skipAnimation
              margin={{
                left: 100,
                right: 100,
                top: 100,
                bottom: 100,
              }}
              tooltip={{ trigger: "none" }}
              series={[
                {
                  data: chartData,
                  innerRadius: 80,
                  outerRadius: 100,
                  paddingAngle: 0,
                  highlightScope: { faded: "global", highlighted: "item" },
                },
              ]}
              height={220}
              width={280}
              slotProps={{
                legend: { hidden: true },
              }}
              onItemClick={(_event, d) => {
                const clickedItem = level2Data.filter(item => item.level3Count > 0)[d.dataIndex];
                if (clickedItem && onRegionSelect) {
                  onRegionSelect(clickedItem.idRegion);
                }
              }}
            >
              <PieCenterLabel
                primaryText={String(totalLevel3Regions)}
                secondaryText={t("regions.charts.totalRegions", "Toplam Bölge")}
              />
            </PieChart>
          </Box>
        )}

      {loading && (
        <Typography variant="body2" sx={{ textAlign: "center", py: 1 }}>
          {t("common.loading", "Yükleniyor...")}
        </Typography>
      )}

      {error && (
        <Typography
          variant="body2"
          color="error"
          sx={{ textAlign: "center", py: 1 }}
        >
          {error}
        </Typography>
      )}

      {!loading && !error && level2Data.length === 0 && (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", py: 1, color: "text.secondary" }}
        >
          {t("region.noLevel2Regions", "Level 2 bölge bulunamadı")}
        </Typography>
      )}

      {/* Level 2 listesi */}
      {level2Data.map((item, index) => (
          <Stack
            key={item.idRegion || index}
            direction="row"
            onClick={() => onRegionSelect && onRegionSelect(item.idRegion)}
            sx={{
              alignItems: "center",
              gap: 2,
              pb: 2,
              cursor: "pointer",
              backgroundColor:
                selectedRegionId === item.idRegion
                  ? "action.selected"
                  : "transparent",
              p: 1,
              borderRadius: 1,
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            {item.isEmpty ? (
              <FolderOffIcon sx={{ color: "#9E9E9E" }} />
            ) : (
              <AccountTreeIcon sx={{ color: item.color }} />
            )}
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: "500" }}>
                  {item.name}
                  {item.isEmpty && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, color: "text.secondary" }}
                    >
                      ({t("region.noLevel3", "Alt bölge yok")})
                    </Typography>
                  )}
                </Typography>
                {getCurrentTenant().projectCode !== "RCU" && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {item.level3Count} {t("region.subRegions", "alt bölge")}
                  </Typography>
                )}
              </Stack>
              <LinearProgress
                variant="determinate"
                aria-label={`${item.name} level 3 region distribution`}
                value={
                  totalLevel3Regions > 0
                    ? (item.level3Count / totalLevel3Regions) * 100
                    : 0
                }
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: item.color,
                  },
                }}
              />
            </Stack>
          </Stack>
        ))}

    </Paper>
  );
}

// Level 3 Region ve Node Chart
export default function RegionNodeChart({ selectedParentRegionId }) {
  const { t } = useTranslation();
  const { isRunning } = useRefreshInterval();
  const isPageVisibility = usePageVisibility();
  const [regions, setRegions] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true); // Sadece ilk yükleme için
  const [error, setError] = useState(null);
  const [regionNodeData, setRegionNodeData] = useState([]);

  // Verileri çek
  const fetchData = async (isInitialLoad = false) => {
    // Sadece ilk yüklemede loading göster
    if (isInitialLoad) {
      setLoading(true);
    }
    setError(null);
    try {
      const [regionsData, nodesData] = await Promise.all([
        getRegion(),
        getNode(),
      ]);
      setRegions(regionsData || []); // State güncellenir ama UI sarsılmaz
      setNodes(nodesData || []); // State güncellenir ama UI sarsılmaz
    } catch (err) {
      setError(err.message || t('common.errorLoadingData', 'Veri yüklenirken hata oluştu'));
      console.error("Error fetching data:", err);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData(true); // İlk yükleme - RegionNodeChart
  }, []);

  // Auto refresh
  useEffect(() => {
    let interval;
    if (isRunning && isPageVisibility) {
      interval = setInterval(() => fetchData(false), 30000); // 30 saniyede bir yenile
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, isPageVisibility]);

  // RefreshAllData event listener
  useEffect(() => {
    const handler = () => {
      fetchData(false);
    };
    window.addEventListener("refreshAllData", handler);
    return () => window.removeEventListener("refreshAllData", handler);
  }, []);

  // 401 hatası kontrolü
  useEffect(() => {
    if (error && error.includes("401")) {
      console.log("Authentication error (401) detected, logging out user");
      localStorage.removeItem("authToken");
      window.location.href = "/login";
    }
  }, [error]);

  // Seçili parent region'ın ismini bul
  const selectedParentRegionName = useMemo(() => {
    if (selectedParentRegionId && regions.length > 0) {
      const parentRegion = regions.find(
        (region) => region.idRegion === selectedParentRegionId
      );
      return parentRegion?.name || parentRegion?.code || "";
    }
    return "";
  }, [selectedParentRegionId, regions]);

  // Region ve Node verilerini birleştir
  useEffect(() => {
    if (regions.length > 0 && nodes.length > 0) {
      // Level 3 region'ları filtrele
      let level3Regions = regions.filter(
        (region) => calculateRegionLevel(region, regions) === 3
      );

      // Eğer parent region seçilmişse, sadece o parent'a ait level 3'leri göster
      if (selectedParentRegionId) {
        level3Regions = level3Regions.filter(
          (region) => region.parentRegion === selectedParentRegionId
        );
      }

      // Her region için node sayısını hesapla
      const regionNodeCounts = level3Regions.map((region, index) => {
        const nodeCount = nodes.filter(
          (node) => node.region_idRegion === region.idRegion
        ).length;

        return {
          idRegion: region.idRegion,
          name: region.name || region.code || `Region ${region.idRegion}`,
          nodeCount: nodeCount,
          color:
            nodeCount > 0
              ? COLOR_PALETTE_LEVEL3[index % COLOR_PALETTE_LEVEL3.length]
              : "#9E9E9E",
          isUnassigned: nodeCount === 0,
        };
      });

      // Node'u olmayan region'ları da dahil et
      setRegionNodeData(regionNodeCounts);
    }
  }, [regions, nodes, selectedParentRegionId]);

  // Chart için veri hazırla
  const chartData = regionNodeData
    .filter((item) => item.nodeCount > 0) // Sadece node'u olanları chart'ta göster
    .map((item) => ({
      label: item.name,
      value: item.nodeCount,
      color: item.color,
    }));

  const totalNodes = regionNodeData.reduce(
    (sum, item) => sum + item.nodeCount,
    0
  );

  const unassignedRegionsCount = regionNodeData.filter(
    (item) => item.isUnassigned
  ).length;

  return (
    <Paper
      elevation={3}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        overflow: "auto",
        maxHeight: "800px",
        p: 2,
      }}
    >
      {selectedParentRegionId && (
        <Typography
          variant="h6"
          sx={{ mb: 1.5, fontSize: "1.1rem", fontWeight: 600 }}
        >
          {t('regions.charts.subRegionNodeDistribution', 'Alt Bölge - Otomat')} {selectedParentRegionName && `(${selectedParentRegionName})`}
        </Typography>
      )}

      {!selectedParentRegionId && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                backgroundColor: "primary.light",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": {
                    opacity: 0.6,
                    transform: "scale(1)",
                  },
                  "50%": {
                    opacity: 1,
                    transform: "scale(1.05)",
                  },
                },
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    color: "primary.contrastText",
                    fontWeight: 300,
                  }}
                >
                  ↑
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="h6"
              sx={{
                mt: 3,
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              {t(
                "regions.charts.selectRegionPrompt",
                "Lütfen yukarıdan bir bölge seçin"
              )}
            </Typography>
          </Box>
        )}

      {selectedParentRegionId && chartData.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PieChart
              skipAnimation
              margin={{
                left: 100,
                right: 100,
                top: 100,
                bottom: 100,
              }}
              tooltip={{ trigger: "none" }}
              series={[
                {
                  data: chartData,
                  innerRadius: 80,
                  outerRadius: 100,
                  paddingAngle: 0,
                  highlightScope: { faded: "global", highlighted: "item" },
                },
              ]}
              height={220}
              width={280}
              slotProps={{
                legend: { hidden: true },
              }}
            >
              <PieCenterLabel
                primaryText={String(totalNodes)}
                secondaryText={t('regions.charts.totalNodes', 'Toplam Otomat')}
              />
            </PieChart>
          </Box>
        )}

      {loading && (
        <Typography variant="body2" sx={{ textAlign: "center", py: 1 }}>
          {t("common.loading", "Yükleniyor...")}
        </Typography>
      )}

      {error && (
        <Typography
          variant="body2"
          color="error"
          sx={{ textAlign: "center", py: 1 }}
        >
          {error}
        </Typography>
      )}

      {!loading &&
        !error &&
        selectedParentRegionId &&
        regionNodeData.length === 0 && (
          <Typography
            variant="body2"
            sx={{ textAlign: "center", py: 1, color: "text.secondary" }}
          >
            {t("region.noLevel3Regions", "Bu bölgede alt bölge bulunamadı")}
          </Typography>
        )}

      {/* Region listesi */}
      {selectedParentRegionId && regionNodeData.map((item, index) => (
          <Stack
            key={item.idRegion || index}
            direction="row"
            sx={{ alignItems: "center", gap: 2, pb: 2 }}
          >
            {item.isUnassigned ? (
              <FolderOffIcon sx={{ color: "#9E9E9E" }} />
            ) : (
              <DeviceHubIcon sx={{ color: item.color }} />
            )}
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: "500" }}>
                  {item.name}
                  {item.isUnassigned && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, color: "text.secondary" }}
                    >
                      ({t("region.unassigned", "Atanmamış")})
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {item.nodeCount}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                aria-label={`${item.name} region node distribution`}
                value={totalNodes > 0 ? (item.nodeCount / totalNodes) * 100 : 0}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: item.color,
                  },
                }}
              />
            </Stack>
          </Stack>
        ))}

      {/* Atanmamış region'lar için özet bilgi */}
      {selectedParentRegionId && unassignedRegionsCount > 0 && (
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t(
              "region.unassignedRegionsInfo",
              `${unassignedRegionsCount} bölgede terminal bulunmamaktadır`
            )}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

RegionNodeChart.propTypes = {
  selectedParentRegionId: PropTypes.number,
};
